import { fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import queryString from 'query-string';
import { enqueueSnackbar } from 'notistack';
//
import config from '../../config';
import { invalidateStores, setToken } from '../slice/auth.slice';
import { userApi } from '../api/user.api';
import { QUERY_KEY_ENTITY } from '../../constants';

const DEFAULT_MAX_RETRIES = 3;
const query = fetchBaseQuery({
  baseUrl: config.REST_API,
  responseHandler: 'content-type',
  paramsSerializer: (params) =>
    queryString.stringify(params, {
      arrayFormat: 'none',
    }),
  prepareHeaders: (headers, api) => {
    const token = api.getState().auth.authToken;
    headers.set('Authorization', token);
    return headers;
  },
});

const baseQueryWithAuth = async (args, api, extraOptions) => {
  const result = await query(args, api, extraOptions);
  let shouldFail = false;
  // we're not retrying mutations - those are usually initiated on user action, and error will be shown
  if (api.type === 'mutation' && result.error) {
    shouldFail = true;
  }
  // is user is unauthorized or has permissions issue - means we need to force him to relogin to refresh permissions on UI
  if (result.error?.status === 401 || result.error?.status === 403) {
    // resetting auth token, so user sees login page
    api.dispatch(setToken(null));
    api.dispatch(
      userApi.util.invalidateTags([{ type: QUERY_KEY_ENTITY.CURRENT_USER }]),
    );
    // need to reset the stores as well, to make sure user won't see cached data
    api.dispatch(invalidateStores());
    notifyAuthIssue(result.error.status);
    shouldFail = true;
  }

  if (shouldFail) {
    // explicitly forwarding auth errors - without this it'd return 'Aborted' error, consuming underlying one
    retry.fail(result.error, result.meta);
  }
  return result;
};

export const customBaseQuery = retry(baseQueryWithAuth, {
  maxRetries: DEFAULT_MAX_RETRIES,
  retryCondition: (
    error,
    args,
    { extraOptions: { maxRetries = DEFAULT_MAX_RETRIES } = {}, attempt },
  ) => {
    return (
      // retrying only if it's network error
      (['FETCH_ERROR', 'TIMEOUT_ERROR'].includes(error.status) ||
        //  or if it's server error (500+) - 'expected' errors (<500) will return same result anyway, so no point in retrying
        (typeof error.status === 'number' && error.status >= 500)) &&
      // and only if we're within attempt limits
      attempt < maxRetries
    );
  },
});

const notifyAuthIssue = (errorCode) => {
  switch (errorCode) {
    case 401: {
      return enqueueSnackbar({ message: 'Unauthorized', variant: 'error' });
    }
    case 403: {
      return enqueueSnackbar({
        message: 'Insufficient privileges',
        variant: 'error',
      });
    }
  }
};
